"use client"

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { captureException } from "@/lib/error-reporter"
import {
  isChunkLoadError,
  recoverFromChunkError,
  retryChunkProbe,
  type RecoveryOutcome,
} from "@/lib/chunk-recovery"

interface ErrorBoundaryUIProps {
  error: Error & { digest?: string }
  /** Route-level grouping tag (e.g. "trade", "admin") — carried on the
   *  /api/log/frontend-error event so Kibana can aggregate per route. */
  routeTag?: string
  /** URL for "Go to Dashboard" button. If omitted, only "Reload Page" is shown. */
  dashboardUrl?: string
}

/**
 * What the user is looking at.
 *
 *  diagnosing — a chunk failed; we are probing the server to find out whether
 *               a reload would fix it. Sub-second in the normal case, so this
 *               renders the same calm "updating" copy rather than flashing an
 *               error the user never needed to see.
 *  reloading  — probe said recoverable and nothing critical is in flight; the
 *               document is on its way out.
 *  prompt     — recoverable, but reloading right now would destroy user work
 *               (order sheet open) or we already spent this build's one
 *               automatic reload. The user decides.
 *  offline    — the server could not be reached. A reload would strand them,
 *               so we show connection copy and a manual retry.
 *  generic    — not a chunk error. The pre-existing failure UI.
 */
type Phase = "diagnosing" | "reloading" | "prompt" | "offline" | "generic"

function outcomeToPhase(outcome: RecoveryOutcome): Phase {
  switch (outcome) {
    case "reloading":
      return "reloading"
    case "deferred-critical":
    case "deferred-exhausted":
      return "prompt"
    case "offline":
      return "offline"
    default:
      return "generic"
  }
}

export function ErrorBoundaryUI({ error, routeTag, dashboardUrl }: ErrorBoundaryUIProps) {
  const isChunk = isChunkLoadError(error)
  const [phase, setPhase] = useState<Phase>(isChunk ? "diagnosing" : "generic")
  /** Set when a user-pressed "Try Again" probed and the link was still dead.
   *  Drives the copy so a second press does not look identical to the first. */
  const [retryFailed, setRetryFailed] = useState(false)
  /** A user-pressed retry is in flight — the probe can take a few seconds on
   *  the very links this screen exists for, so the button must not look inert. */
  const [checking, setChecking] = useState(false)

  useEffect(() => {
    let alive = true

    if (!isChunk) {
      captureException(error, { routeTag })
      return
    }

    // Chunk failures are still reported — they are the signal that tells us a
    // deploy stranded someone or that a network is failing at scale. The
    // outcome rides along so Kibana can separate "self-healed" from "user was
    // left with a dead screen".
    void recoverFromChunkError(error).then((outcome) => {
      captureException(error, {
        routeTag,
        component: "chunk_load_error",
        // First-class field, not `extra` — `extra` is never copied into the
        // outbound payload, so every chunk event on prod arrived with no
        // outcome at all and "self-healed" was indistinguishable from
        // "trader left on a dead screen".
        recoveryOutcome: outcome,
      })
      // On "reloading" the document is being torn down; leave the updating
      // copy on screen rather than flipping to something else mid-unload.
      if (alive && outcome !== "reloading") setPhase(outcomeToPhase(outcome))
    })

    return () => {
      alive = false
    }
  }, [error, routeTag, isChunk])

  /**
   * "Try Again" on the offline screen. Re-probes instead of reloading: on a
   * link that is still dead a reload destroys the page and lands the trader on
   * the offline shell, which is the opposite of the "nothing has been lost"
   * promise this screen makes. We only tear the document down once the server
   * has actually answered.
   */
  async function handleOfflineRetry() {
    setChecking(true)
    setRetryFailed(false)
    const kind = await retryChunkProbe(error)
    if (kind === "offline") {
      setChecking(false)
      setRetryFailed(true)
      return
    }
    window.location.reload()
  }

  if (checking) return <UpdatingScreen variant="reconnect" />
  if (phase === "diagnosing" || phase === "reloading") {
    return <UpdatingScreen />
  }

  const isOffline = phase === "offline"
  const title = isOffline
    ? retryFailed
      ? "Still no connection"
      : "Connection interrupted"
    : phase === "prompt"
      ? "A new version is available"
      : "Something went wrong"
  const body = isOffline
    ? retryFailed
      ? "We still couldn't reach the server. You're safe to wait here — nothing you've entered has been lost."
      : "We couldn't reach the server. Check your connection and try again — nothing you've entered has been lost."
    : phase === "prompt"
      ? "This page is running an older version of the app. Reload when you're ready — any details you've entered will be cleared."
      : "An unexpected error occurred. Our team has been notified."
  const primaryLabel = isOffline ? "Try Again" : phase === "prompt" ? "Reload Now" : "Reload Page"

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background/95 p-4 text-center backdrop-blur-sm"
    >
      <div className="mx-auto w-full max-w-sm space-y-5 rounded-2xl border border-border bg-card p-4 shadow-2xl sm:p-6 md:p-8">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
          <svg
            className="h-8 w-8 text-destructive"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <div>
          <h2 className="text-xl font-bold tracking-tight text-foreground">{title}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{body}</p>
          {error.digest && phase === "generic" && (
            <p className="mt-1 text-xs text-muted-foreground/60">Reference: {error.digest}</p>
          )}
        </div>
        <div className={dashboardUrl ? "flex flex-col gap-2 pt-2" : "pt-2"}>
          <Button
            onClick={isOffline ? handleOfflineRetry : () => window.location.reload()}
            className="h-11 w-full rounded-lg text-sm font-semibold shadow-sm"
          >
            {primaryLabel}
          </Button>
          {dashboardUrl && (
            <Button
              variant="outline"
              onClick={() => window.location.replace(dashboardUrl)}
              className="h-11 w-full rounded-lg text-sm font-semibold"
            >
              Go to Dashboard
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Shown while we diagnose and while the reload is in flight. Deliberately not
 * an error: in the overwhelmingly common case (a deploy replaced the chunk)
 * this resolves itself in a second or two, and showing "Critical System Error"
 * for that is what made a routine deploy look like an outage.
 */
function UpdatingScreen({ variant = "update" }: { variant?: "update" | "reconnect" }) {
  const heading =
    variant === "reconnect" ? "Checking your connection" : "Updating to the latest version"
  const sub =
    variant === "reconnect"
      ? "Hang on a moment — we're trying to reach the server…"
      : "This will only take a moment…"
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background/95 p-4 text-center backdrop-blur-sm"
    >
      <div className="mx-auto w-full max-w-sm space-y-4 rounded-2xl border border-border bg-card p-6 shadow-2xl md:p-8">
        <div
          className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary"
          aria-hidden="true"
        />
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-foreground">{heading}</h2>
          <p className="mt-1.5 text-sm text-muted-foreground">{sub}</p>
        </div>
      </div>
    </div>
  )
}
